// Cuentas de la vista previa de Marketing: qué forma va a tener cada pieza en
// cada red, para que lo que se ve antes de publicar sea lo que sale.
//
// Los límites tienen que ser los mismos que aplica el servidor al preparar la
// imagen (SocialPosts::MetaImageService, en app/services/social_posts/). Si se
// tocan en un lado y no en el otro, la vista previa vuelve a mentir.

// Instagram rechaza una imagen de feed fuera de este rango: más alta que 4:5 o
// más ancha que 1.91:1. El servidor la recorta al centro al límite más cercano.
export const INSTAGRAM_MIN_RATIO = 4 / 5;
export const INSTAGRAM_MAX_RATIO = 1.91;

// El cuadro que ocupa una publicación en el feed de Instagram. Una imagen que no
// lo llena queda con el hueco relleno por una copia desenfocada de sí misma.
export const INSTAGRAM_FRAME_RATIO = 4 / 5;

// Un reel siempre es vertical.
export const REEL_RATIO = 9 / 16;

// Menos que esto es redondeo, no una diferencia que se vea.
const RATIO_TOLERANCE = 0.01;

export const MEDIA_KINDS = Object.freeze({
  IMAGE: 'image',
  CAROUSEL: 'carousel',
  REEL: 'reel',
});

export const CAROUSEL_MAX_ITEMS = 10;

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.m4v'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'];
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const hasExtension = (file, extensions) =>
  extensions.some(extension =>
    (file.name ?? '').toLowerCase().endsWith(extension)
  );

// Hay combinaciones de sistema y navegador que mandan application/octet-stream
// para un archivo perfectamente válido, así que se mira también la extensión.
// El servidor hace lo mismo (Marketing::CampaignCreatorService#video_file?).
export const isVideoFile = file =>
  (file.type ?? '').startsWith('video/') ||
  hasExtension(file, VIDEO_EXTENSIONS);

export const isImageFile = file =>
  IMAGE_TYPES.includes(file.type) || hasExtension(file, IMAGE_EXTENSIONS);

// El tipo de pieza sale de lo que se subió, para que el operador no tenga que
// elegirlo antes: un video es un reel, varias imágenes son un carrusel.
// Devuelve el código de error en vez del texto, que lo traduce el componente.
export const inferMediaKind = files => {
  if (!files.length) return { kind: null, error: null };

  const videos = files.filter(isVideoFile);
  if (videos.length && videos.length !== files.length) {
    return { kind: null, error: 'MIXED' };
  }
  if (videos.length > 1) return { kind: null, error: 'MULTIPLE_VIDEOS' };
  if (videos.length === 1) return { kind: MEDIA_KINDS.REEL, error: null };

  if (!files.every(isImageFile)) return { kind: null, error: 'UNSUPPORTED' };
  if (files.length > CAROUSEL_MAX_ITEMS) {
    return { kind: null, error: 'TOO_MANY' };
  }

  return {
    kind: files.length > 1 ? MEDIA_KINDS.CAROUSEL : MEDIA_KINDS.IMAGE,
    error: null,
  };
};

export const clampToInstagram = ratio =>
  Math.min(Math.max(ratio, INSTAGRAM_MIN_RATIO), INSTAGRAM_MAX_RATIO);

// La proporción con la que sale una imagen en Instagram. Instagram tiene su propia
// copia (SocialPost#instagram_meta_media): nada de esto toca a Facebook, que
// publica la imagen con su forma original. Antes era una sola copia para las dos
// redes, y recortar para Instagram arruinaba la imagen en Facebook.
//
// "Llenar el cuadro" la lleva a 4:5 para que ocupe todo el cuadro de Instagram
// y no quede hueco que rellenar con el fondo desenfocado.
export const instagramRatio = (naturalRatio, { fill = false } = {}) => {
  if (fill) return INSTAGRAM_FRAME_RATIO;
  if (!naturalRatio) return INSTAGRAM_FRAME_RATIO;

  return clampToInstagram(naturalRatio);
};

// En un carrusel Instagram muestra todas las fotos con la proporción de la
// primera, así que el servidor las lleva a todas a esa. La vista previa también.
// Facebook no: cada foto conserva la suya.
export const instagramCarouselRatio = (naturalRatios, options = {}) =>
  instagramRatio(naturalRatios[0], options);

// Mientras no se sabe la forma (la imagen todavía se está leyendo), un cuadrado.
export const facebookRatio = naturalRatio => naturalRatio || 1;

export const fillsInstagramFrame = ratio =>
  Math.abs(ratio - INSTAGRAM_FRAME_RATIO) < RATIO_TOLERANCE;

// Lo que se pierde al recortar, para avisarlo antes de publicar.
export const isCropped = (naturalRatio, finalRatio) =>
  Boolean(naturalRatio) &&
  Math.abs(naturalRatio - finalRatio) >= RATIO_TOLERANCE;
